import {
    type KeyboardEvent,
    type MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./TimePicker.module.scss";

/** Rendered copies of the option cycle when the column loops. */
const COPIES = 3;

const mod = (n: number, m: number) => ((n % m) + m) % m;

export interface TimeColumnProps {
    ariaLabel: string;
    /** Base id used for `aria-activedescendant`. */
    idBase: string;
    /** Display labels in natural order (e.g. `01`…`12`). */
    options: string[];
    /** Index of the current value, or `-1` when there is none. */
    selectedIndex: number;
    onSelectIndex: (index: number) => void;
    /** Tab / Shift+Tab inside the flyout moves between columns. */
    onColumnNav: (dir: -1 | 1) => void;
    /** Enter / Space closes the flyout. */
    onConfirm: () => void;
    onFocusColumn: () => void;
    /**
     * Endless wheel behaviour: the cycle is rendered `COPIES` times and the
     * scroll position is kept in the middle copy, so the list never ends.
     * Only possible when the cycle is taller than the viewport — shorter
     * columns (e.g. AM/PM) are rotated instead.
     */
    loop: boolean;
    columnRef: MutableRefObject<HTMLDivElement | null>;
    className?: string;
}

/**
 * One scrollable column of the TimePicker flyout.
 *
 * The current value always sits on the first row: looping columns scroll to
 * it, short columns rotate their options around it. Keyboard model mirrors a
 * wheel picker — the column is a single tab stop (`role="listbox"`), arrows
 * move the value and the highlighted row keeps its place on screen while the
 * options scroll under it.
 */
export const TimeColumn = ({
    ariaLabel,
    idBase,
    options,
    selectedIndex,
    onSelectIndex,
    onColumnNav,
    onConfirm,
    onFocusColumn,
    loop,
    columnRef,
    className,
}: TimeColumnProps) => {
    const firstItemRef = useRef<HTMLButtonElement | null>(null);
    const secondItemRef = useRef<HTMLButtonElement | null>(null);
    const length = options.length;
    const safeIndex = selectedIndex < 0 ? 0 : selectedIndex;

    /**
     * Index rendered on the first row. Looping columns keep the natural order
     * (the scroll position does the work); the rest rotate so the current
     * value leads the list. Frozen while the flyout stays open so navigating
     * does not reshuffle the rows.
     */
    const [rotation] = useState(() => (loop ? 0 : safeIndex));

    const copies = loop ? COPIES : 1;
    /** Copy that carries the real listbox semantics; the rest are decorative. */
    const realCopy = loop ? 1 : 0;

    /** Row of a value inside one copy, after rotation. */
    const positionOf = useCallback(
        (index: number) => mod(index - rotation, length),
        [length, rotation],
    );

    /**
     * `rowH` is the item height including the flex gap, so a whole cycle is
     * `rowH * length` — exact, unlike deriving it from `scrollHeight`.
     */
    const metrics = useCallback(() => {
        const el = columnRef.current;
        const first = firstItemRef.current;
        const second = secondItemRef.current;
        if (!el || !first) return null;
        const rowH = second ? second.offsetTop - first.offsetTop : 0;
        if (rowH <= 0) return null;
        return { el, base: first.offsetTop, rowH, cycle: rowH * length };
    }, [columnRef, length]);

    /** Puts a row at the top of the viewport (open / off-screen states). */
    const anchorRow = useCallback(
        (row: number) => {
            const m = metrics();
            if (!m) return;
            m.el.scrollTop = m.base + row * m.rowH + (loop ? m.cycle : 0);
        },
        [loop, metrics],
    );

    /** Keeps the scroll position inside the middle copy — copies are identical. */
    const normalize = useCallback(() => {
        if (!loop) return;
        const m = metrics();
        if (!m) return;
        const rel = m.el.scrollTop - m.base;
        if (rel < m.cycle || rel >= m.cycle * 2) {
            m.el.scrollTop = m.base + m.cycle + mod(rel, m.cycle);
        }
    }, [loop, metrics]);

    // The flyout mounts on open, so this anchors the current value once.
    useEffect(() => {
        const frame = window.requestAnimationFrame(() =>
            anchorRow(positionOf(safeIndex)),
        );
        return () => window.cancelAnimationFrame(frame);
    }, []);

    const step = (dir: -1 | 1, nextIndex: number) => {
        const m = metrics();
        if (!m) return;
        const { el, base, rowH, cycle } = m;
        const nextRow = positionOf(nextIndex);

        if (!loop) {
            const top = base + nextRow * rowH;
            if (top < el.scrollTop) el.scrollTop = top;
            else if (top + rowH > el.scrollTop + el.clientHeight) {
                el.scrollTop = top + rowH - el.clientHeight;
            }
            return;
        }

        const rel = el.scrollTop - base;
        const offsetInView = mod(positionOf(safeIndex) * rowH - rel, cycle);
        const wasVisible = offsetInView <= el.clientHeight - rowH;
        if (!wasVisible) {
            anchorRow(nextRow);
            return;
        }
        el.scrollTop += dir * rowH;
        normalize();
    };

    /** One row at a time: the highlight stays put and the options scroll. */
    const select = (dir: -1 | 1, nextIndex: number) => {
        step(dir, nextIndex);
        onSelectIndex(nextIndex);
    };

    /** Multi-row jump (Home / End): re-anchor instead of scrolling one row. */
    const jumpTo = (nextIndex: number) => {
        anchorRow(positionOf(nextIndex));
        onSelectIndex(nextIndex);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const dir = e.key === "ArrowDown" ? 1 : -1;
            select(dir, mod(safeIndex + dir, length));
            return;
        }
        if (e.key === "Tab") {
            // Stop Radix FocusScope from re-routing Tab after we move focus.
            e.preventDefault();
            e.stopPropagation();
            onColumnNav(e.shiftKey ? -1 : 1);
            return;
        }
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onConfirm();
            return;
        }
        if (e.key === "Home") {
            e.preventDefault();
            jumpTo(mod(rotation, length));
            return;
        }
        if (e.key === "End") {
            e.preventDefault();
            jumpTo(mod(rotation + length - 1, length));
        }
    };

    return (
        <div
            aria-activedescendant={
                selectedIndex >= 0
                    ? `${idBase}-opt-${selectedIndex}`
                    : undefined
            }
            aria-label={ariaLabel}
            className={cx(styles.column, className)}
            onFocus={onFocusColumn}
            onKeyDown={onKeyDown}
            onScroll={normalize}
            ref={columnRef}
            role="listbox"
            tabIndex={0}
        >
            {Array.from({ length: copies * length }, (_, row) => {
                const index = mod(rotation + row, length);
                const selected = index === selectedIndex;
                const isReal = Math.floor(row / length) === realCopy;
                return (
                    <button
                        aria-hidden={isReal ? undefined : true}
                        aria-selected={isReal ? selected : undefined}
                        className={cx(
                            styles.option,
                            selected && styles.optionSelected,
                        )}
                        id={isReal ? `${idBase}-opt-${index}` : undefined}
                        key={row}
                        onClick={() => onSelectIndex(index)}
                        onMouseDown={e => {
                            // Keep the column as the focus holder so the
                            // arrows keep working after a mouse pick.
                            e.preventDefault();
                            columnRef.current?.focus();
                        }}
                        ref={
                            row === 0
                                ? firstItemRef
                                : row === 1
                                ? secondItemRef
                                : undefined
                        }
                        role={isReal ? "option" : "presentation"}
                        tabIndex={-1}
                        type="button"
                    >
                        {options[index]}
                    </button>
                );
            })}
        </div>
    );
};

TimeColumn.displayName = "TimeColumn";
